
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { Check, RefreshCcw } from 'lucide-react';

interface TremendousIntegrationSettingsProps {
  isEditing?: boolean;
}

const TremendousIntegrationSettings: React.FC<TremendousIntegrationSettingsProps> = ({ 
  isEditing = false 
}) => {
  const { toast } = useToast();
  const [apiKey, setApiKey] = useState('••••••••••••••••••••');
  const [organizationId, setOrganizationId] = useState('TREM-ORG-12345');
  const [isConnected, setIsConnected] = useState(true);
  const [isTesting, setIsTesting] = useState(false);
  const [autoFulfillment, setAutoFulfillment] = useState(true);
  const [notifyRecipient, setNotifyRecipient] = useState(true);
  
  const handleTestConnection = async () => {
    setIsTesting(true);
    
    // Simulate API call
    setTimeout(() => {
      toast({
        title: "Connection Successful",
        description: "Successfully connected to Tremendous API",
      });
      setIsConnected(true);
      setIsTesting(false);
    }, 1500);
  };
  
  return (
    <div className="space-y-4">
      {isConnected ? (
        <div className="p-4 bg-green-50 border border-green-100 rounded-md space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Check className="h-5 w-5 text-green-600 mr-2" />
              <div>
                <p className="font-medium">Connected to Tremendous</p>
                <p className="text-sm text-gray-500">API connection is active and working</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={handleTestConnection} disabled={isTesting}>
              {isTesting ? (
                <><RefreshCcw className="h-4 w-4 mr-2 animate-spin" /> Testing</>
              ) : (
                "Test Connection"
              )}
            </Button>
          </div>
        </div>
      ) : null}

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="tremendous-api-key">API Key</Label>
          <Input
            id="tremendous-api-key"
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="Enter your Tremendous API key"
            disabled={!isEditing}
          />
          <p className="text-xs text-muted-foreground">
            Your Tremendous API key found in your account settings
          </p>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="tremendous-org-id">Organization ID</Label>
          <Input
            id="tremendous-org-id"
            value={organizationId}
            onChange={(e) => setOrganizationId(e.target.value)}
            placeholder="Enter your Tremendous Organization ID"
            disabled={!isEditing}
          />
          <p className="text-xs text-muted-foreground">
            Your Tremendous Organization ID found in your account settings
          </p>
        </div>
      </div>
      
      <div className="space-y-4">
        <div className="flex items-center justify-between border rounded-md p-4">
          <div className="space-y-0.5">
            <Label htmlFor="auto-fulfillment">Auto-Fulfill Rewards</Label>
            <p className="text-sm text-muted-foreground">
              Automatically fulfill rewards when triggered
            </p>
          </div>
          <Switch
            id="auto-fulfillment"
            checked={autoFulfillment}
            onCheckedChange={setAutoFulfillment}
            disabled={!isEditing}
          />
        </div>
        
        <div className="flex items-center justify-between border rounded-md p-4">
          <div className="space-y-0.5">
            <Label htmlFor="notify-recipient">Notify Recipient</Label>
            <p className="text-sm text-muted-foreground">
              Send email notification to recipient when reward is sent
            </p>
          </div>
          <Switch
            id="notify-recipient"
            checked={notifyRecipient}
            onCheckedChange={setNotifyRecipient}
            disabled={!isEditing}
          />
        </div>
      </div>
    </div>
  );
};

export default TremendousIntegrationSettings;
